import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, FileCode2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, Panel, Pill, Empty, inputCls } from "@/components/kit";
import { EmbedFrame } from "@/components/EmbedFrame";
import { api, errMsg } from "@/lib/api";
import { FormDialog, SectionHead } from "./common";
import { UpgradeGate } from "@/components/UpgradeGate";

const BLANK = { title: "", description: "", code: "", placement: "both", height: 640 };
const WHERE = { fan: "Fan app only", player: "Player app only", both: "Fan & player apps" };

const EmbedForm = ({ f, setF }) => (
  <>
    <Field label="Title"><Input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="e.g. Summer camp sign-up" data-testid="embed-title-input" /></Field>
    <Field label="Short description (optional)"><Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="embed-description-input" /></Field>
    <Field label="Embed code" hint="Paste the embed code from Google Forms, Jotform, Typeform, Tally, Microsoft Forms, etc.">
      <textarea required className={`${inputCls} h-36 font-mono text-xs`} value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} placeholder='<iframe src="https://docs.google.com/forms/..." ...></iframe>' data-testid="embed-code-input" />
    </Field>
    <div className="grid grid-cols-2 gap-4">
      <Field label="Show in">
        <select className={inputCls} value={f.placement} onChange={(e) => setF({ ...f, placement: e.target.value })} data-testid="embed-placement-select">
          <option value="both">Fan & player apps</option><option value="fan">Fan app only</option><option value="player">Player app only</option>
        </select>
      </Field>
      <Field label="Height (px)"><Input type="number" min="150" max="3000" value={f.height} onChange={(e) => setF({ ...f, height: e.target.value })} data-testid="embed-height-input" /></Field>
    </div>
    {f.code && <div><p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Preview</p><EmbedFrame embed={{ ...f, height: Math.min(Number(f.height) || 400, 400) }} /></div>}
  </>
);

export default function Embeds({ teamId, overview, setTab }) {
  if (!overview.features.embeds) return <UpgradeGate feature="embeds" setTab={setTab} />;
  return <EmbedsInner teamId={teamId} />;
}

function EmbedsInner({ teamId }) {
  const [embeds, setEmbeds] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(BLANK);

  const load = () => api.get(`/teams/${teamId}/embeds`).then((r) => setEmbeds(r.data));
  useEffect(() => {
    load();
  }, [teamId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    const body = { ...f, height: Number(f.height) || 640 };
    try {
      if (editing) await api.put(`/teams/${teamId}/embeds/${editing.id}`, body);
      else await api.post(`/teams/${teamId}/embeds`, body);
      toast.success("Form saved");
      setOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (e) => {
    if (!window.confirm(`Remove ${e.title}?`)) return;
    try {
      await api.delete(`/teams/${teamId}/embeds/${e.id}`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="space-y-6">
      <SectionHead title="Forms & embeds" text="Add external forms (registrations, surveys, kit orders) to your fan app, player app or both.">
        <Button className="rounded-full font-bold" onClick={() => { setEditing(null); setF(BLANK); setOpen(true); }} data-testid="add-embed-btn"><Plus className="mr-1 h-4 w-4" /> Add form</Button>
      </SectionHead>
      {!embeds.length && <Empty icon={FileCode2} title="No forms yet" text="Paste an embed code from any form builder to show it inside your apps." />}
      <div className="grid gap-5 xl:grid-cols-2">
        {embeds.map((e) => (
          <Panel key={e.id} data-testid={`admin-embed-${e.id}`}>
            <div className="mb-4 flex items-start justify-between gap-3">
              <div><Pill tone="volt">{WHERE[e.placement]}</Pill><p className="mt-2 font-display text-2xl font-bold uppercase text-white">{e.title}</p>{e.description && <p className="text-sm text-slate-400">{e.description}</p>}</div>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" onClick={() => { setEditing(e); setF(e); setOpen(true); }} data-testid={`edit-embed-${e.id}`}><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" className="text-rose-400" onClick={() => del(e)} data-testid={`delete-embed-${e.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
            <EmbedFrame embed={{ ...e, height: Math.min(e.height, 420) }} />
          </Panel>
        ))}
      </div>
      <FormDialog open={open} onOpenChange={setOpen} title={editing ? "Edit form" : "Add form"} onSubmit={save} testid="embed-form"><EmbedForm f={f} setF={setF} /></FormDialog>
    </div>
  );
}
